/** Shared canvas inline video preview — POSTER-FIRST / ACTIVE-PLAYER (V7)
 * + SINGLE_PLAYBACK_OWNER / hover+pause+modal handoff (V8).
 *
 * V8 deltas on top of V7:
 *  - VideoPlaybackCoordinator claims CANVAS_HOVER / CANVAS_EXPLICIT / yields to MODAL
 *  - Hover leave no longer races the native `pause` event into a sticky PAUSED
 *    (that bug blocked subsequent hover — phase stuck ≠ IDLE_POSTER)
 *  - Loaded poster drops the V6 shimmer animation (letterbox flicker)
 *  - Play icon is the only play/pause hit target; media body is free for expand
 *  - Modal ownership forces this surface back to IDLE_POSTER (unmount video)
 */

import {
  forwardRef,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
} from 'react'
import { isDrawableVideoFrame } from './videoInitialFramePrime'
import {
  claimPlayback,
  getPlaybackOwner,
  publishPlaybackTime,
  releasePlayback,
  subscribePlayback,
  type PlaybackOwnerKind,
} from './videoPlaybackCoordinator'
import { acquirePosterLoadSlot, releasePosterLoadSlot } from './videoPosterLoadGate'
import {
  computeVisibleSurface,
  resolveVideoPresentationState,
  type VideoPlayerPhase,
} from './videoPreviewPresentation'
import { NODE_MEDIA_SURFACE_CLASS } from './nodeInteractionContract'

export interface CanvasVideoPreviewProps {
  src: string
  poster?: string
  /** Stable asset identity for SINGLE_PLAYBACK_OWNER (URL or task id). */
  assetId?: string
  className?: string
  onLoadedDimensions?: (width: number, height: number) => void
  /** V2 — expanded function card: playback survives pointer leave. */
  stickyPlayback?: boolean
  /** V2 — node drag in progress: never pause/release on synthetic leave. */
  isNodeDragging?: boolean
  /** V2 — incremented by CanvasBoard on outside collapse to force idle. */
  playbackResetKey?: number
}

export type CanvasVideoPreviewHandle = {
  activateStickyPlayback: () => void
}

function isHoverCapableEnvironment(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

function CanvasVideoPreview({
  src,
  poster,
  assetId: assetIdProp,
  className,
  onLoadedDimensions,
  stickyPlayback = false,
  isNodeDragging = false,
  playbackResetKey = 0,
}: CanvasVideoPreviewProps,
ref: React.Ref<CanvasVideoPreviewHandle>) {
  const assetId = assetIdProp || src
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [phase, setPhase] = useState<VideoPlayerPhase>('IDLE_POSTER')
  const [posterBroken, setPosterBroken] = useState(false)
  const [posterLoaded, setPosterLoaded] = useState(false)
  const [videoDrawable, setVideoDrawable] = useState(false)
  /** VistoeraTASK-00139 — the `<img>` never carries a real `src` until a
   * poster-load-gate slot is granted (see `videoPosterLoadGate`), so a
   * Canvas with many un-backfilled historical nodes never fires more than
   * a small fixed number of concurrent poster HTTP requests at once. */
  const [gatedPosterSrc, setGatedPosterSrc] = useState<string | undefined>(undefined)
  const posterSlotHeldRef = useRef(false)
  const posterTimeoutIdRef = useRef<number | undefined>(undefined)
  const [muted, setMuted] = useState(false)
  const phaseRef = useRef<VideoPlayerPhase>('IDLE_POSTER')
  const hoverActiveRef = useRef(false)
  /** When true, ignore the next native `pause` event (hover-leave / modal handoff). */
  const ignorePauseEventRef = useRef(false)
  const ownerTokenRef = useRef<symbol | null>(null)
  const releasingToIdleRef = useRef(false)
  /** Ignore coordinator callbacks while we are mid-claim (claim notifies sync before ref is assigned). */
  const suppressOwnerNotifyRef = useRef(false)
  /** After MODAL closes while pointer is still over the card, browsers often
   * re-fire enter — that must NOT auto-resume (MODAL_CLOSE → IDLE_POSTER). */
  const suppressHoverUntilLeaveRef = useRef(false)
  const yieldedToModalRef = useRef(false)
  const stickyPlaybackRef = useRef(stickyPlayback)
  const isNodeDraggingRef = useRef(isNodeDragging)
  stickyPlaybackRef.current = stickyPlayback
  isNodeDraggingRef.current = isNodeDragging

  const setPhaseSafe = (next: VideoPlayerPhase) => {
    phaseRef.current = next
    setPhase(next)
  }

  const releaseToIdle = useEffectEvent((opts?: { publishTime?: boolean }) => {
    releasingToIdleRef.current = true
    ignorePauseEventRef.current = true
    hoverActiveRef.current = false
    const video = videoRef.current
    if (video && opts?.publishTime !== false) {
      publishPlaybackTime(assetId, video.currentTime || 0)
    }
    if (video && !video.paused) {
      try {
        video.pause()
      } catch {
        /* ignore */
      }
    }
    releasePlayback(assetId, ownerTokenRef.current)
    ownerTokenRef.current = null
    setMuted(false)
    setVideoDrawable(false)
    setPhaseSafe('IDLE_POSTER')
    // Allow a microtask for the native pause event to land under the ignore flag.
    queueMicrotask(() => {
      ignorePauseEventRef.current = false
      releasingToIdleRef.current = false
    })
  })

  useEffect(() => {
    setPosterBroken(false)
    setPosterLoaded(false)
    setVideoDrawable(false)
    setMuted(false)
    hoverActiveRef.current = false
    releasePlayback(assetId, ownerTokenRef.current)
    ownerTokenRef.current = null
    setPhaseSafe('IDLE_POSTER')
  }, [src, assetId])

  useEffect(() => {
    setPosterLoaded(false)
    setPosterBroken(false)
    setGatedPosterSrc(undefined)
    if (posterSlotHeldRef.current) {
      releasePosterLoadSlot()
      posterSlotHeldRef.current = false
    }
    if (!poster) return
    let cancelled = false
    acquirePosterLoadSlot().then(() => {
      if (cancelled) {
        releasePosterLoadSlot()
        return
      }
      posterSlotHeldRef.current = true
      setGatedPosterSrc(poster)
      // VistoeraTASK-00139 — defensive backstop: the backend's lazy
      // poster-backfill route can legitimately take many seconds (R2
      // materialize + ffmpeg decode behind a small fixed concurrency
      // limit), but a held slot MUST eventually free even if this
      // particular request never fires `onLoad`/`onError` at all (a
      // truly dead connection, a backgrounded tab pausing image
      // decoding, etc.) — otherwise one stuck request can starve every
      // other node's poster forever, same failure shape as the
      // `loading="lazy"` deadlock this same task found and removed.
      // Cleared by `releasePosterSlotOnce` on a normal load/error — a
      // second real regression found in this task's own re-verification
      // was this same timer firing *after* a successful load (never
      // cleared), retroactively flipping an already-rendered poster back
      // to `posterBroken` ~20s later.
      posterTimeoutIdRef.current = window.setTimeout(() => {
        setPosterBroken(true)
        releasePosterSlotOnce()
      }, 20000)
    })
    return () => {
      cancelled = true
      if (posterTimeoutIdRef.current !== undefined) {
        window.clearTimeout(posterTimeoutIdRef.current)
        posterTimeoutIdRef.current = undefined
      }
      if (posterSlotHeldRef.current) {
        releasePosterLoadSlot()
        posterSlotHeldRef.current = false
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poster])

  const releasePosterSlotOnce = () => {
    if (posterTimeoutIdRef.current !== undefined) {
      window.clearTimeout(posterTimeoutIdRef.current)
      posterTimeoutIdRef.current = undefined
    }
    if (posterSlotHeldRef.current) {
      releasePosterLoadSlot()
      posterSlotHeldRef.current = false
    }
  }

  // Yield to MODAL (or another owner that isn't us).
  useEffect(() => {
    return subscribePlayback(assetId, (change) => {
      if (suppressOwnerNotifyRef.current) return
      if (change.token && change.token === ownerTokenRef.current) return
      if (change.owner === 'MODAL') {
        yieldedToModalRef.current = true
        // Suppress immediately — Modal close often re-fires enter before NONE notify.
        suppressHoverUntilLeaveRef.current = true
        if (phaseRef.current !== 'IDLE_POSTER' && phaseRef.current !== 'ERROR') {
          releaseToIdle({ publishTime: true })
        }
        return
      }
      if (change.owner === 'NONE' && yieldedToModalRef.current) {
        yieldedToModalRef.current = false
        suppressHoverUntilLeaveRef.current = true
        return
      }
      if (
        change.token &&
        ownerTokenRef.current &&
        (change.owner === 'CANVAS_HOVER' || change.owner === 'CANVAS_EXPLICIT')
      ) {
        releaseToIdle({ publishTime: false })
      }
    })
  }, [assetId, releaseToIdle])

  useEffect(() => {
    return () => {
      releasePlayback(assetId, ownerTokenRef.current)
      ownerTokenRef.current = null
    }
  }, [assetId])

  const presentation = resolveVideoPresentationState({
    phase,
    hasPoster: Boolean(poster),
    posterBroken,
    videoDrawable,
  })

  useEffect(() => {
    if (!presentation.mountVideo) return
    const video = videoRef.current
    if (!video) return

    const onPlaying = () => {
      if (releasingToIdleRef.current) return
      setVideoDrawable(true)
      setPhaseSafe('PLAYING')
      onLoadedDimensions?.(video.videoWidth, video.videoHeight)
      publishPlaybackTime(assetId, video.currentTime || 0)
    }
    const onDrawableProgress = () => {
      if (releasingToIdleRef.current) return
      if (isDrawableVideoFrame(video)) setVideoDrawable(true)
    }
    const onPause = () => {
      if (video.ended) return
      if (ignorePauseEventRef.current || releasingToIdleRef.current) return
      if (phaseRef.current === 'IDLE_POSTER' || phaseRef.current === 'ERROR') return
      hoverActiveRef.current = false
      publishPlaybackTime(assetId, video.currentTime || 0)
      setPhaseSafe('PAUSED')
      // A user-visible pause upgrades hover ownership to explicit so leave won't apply.
      if (ownerTokenRef.current && getPlaybackOwner(assetId) === 'CANVAS_HOVER') {
        suppressOwnerNotifyRef.current = true
        releasePlayback(assetId, ownerTokenRef.current)
        ownerTokenRef.current = claimPlayback(assetId, 'CANVAS_EXPLICIT', video.currentTime || 0)
        suppressOwnerNotifyRef.current = false
      }
    }
    const onEnded = () => {
      hoverActiveRef.current = false
      releasePlayback(assetId, ownerTokenRef.current)
      ownerTokenRef.current = null
      setMuted(false)
      setVideoDrawable(false)
      setPhaseSafe('IDLE_POSTER')
    }
    const onLoadedMetadata = () => onLoadedDimensions?.(video.videoWidth, video.videoHeight)
    const onError = () => {
      releasePlayback(assetId, ownerTokenRef.current)
      ownerTokenRef.current = null
      setPhaseSafe('ERROR')
    }
    const onTimeUpdate = () => publishPlaybackTime(assetId, video.currentTime || 0)

    video.addEventListener('playing', onPlaying)
    video.addEventListener('loadeddata', onDrawableProgress)
    video.addEventListener('canplay', onDrawableProgress)
    video.addEventListener('pause', onPause)
    video.addEventListener('ended', onEnded)
    video.addEventListener('loadedmetadata', onLoadedMetadata)
    video.addEventListener('error', onError)
    video.addEventListener('timeupdate', onTimeUpdate)

    return () => {
      video.removeEventListener('playing', onPlaying)
      video.removeEventListener('loadeddata', onDrawableProgress)
      video.removeEventListener('canplay', onDrawableProgress)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('ended', onEnded)
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('error', onError)
      video.removeEventListener('timeupdate', onTimeUpdate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentation.mountVideo, src, assetId, onLoadedDimensions])

  useEffect(() => {
    if (phase !== 'STARTING') return
    const video = videoRef.current
    if (!video) return
    // If MODAL already owns this asset, do not start.
    if (getPlaybackOwner(assetId) === 'MODAL') {
      releaseToIdle({ publishTime: false })
      return
    }
    video.muted = muted
    void video.play().catch((err: unknown) => {
      // V9 FIX: a quick hover-enter -> hover-leave (very common with real
      // pointer input, e.g. a failed drag attempt) calls video.pause()
      // while this play() promise is still in flight. Chrome/Safari then
      // reject it with AbortError ("interrupted by a call to pause") —
      // that is NOT a real playback error, it's an expected race, and
      // releaseToIdle() has already put the phase back to IDLE_POSTER.
      // Previously this branch unconditionally set phase='ERROR', which
      // permanently disabled hover on that node (tryHoverStart only fires
      // from IDLE_POSTER) until an explicit Play-icon click reset it —
      // this was the FIRST_BROKEN_HOVER_STAGE root cause found in the V9
      // git archaeology / forensic pass.
      if (phaseRef.current !== 'STARTING') return
      const name = (err as { name?: string } | null)?.name
      if (name === 'AbortError') return
      releasePlayback(assetId, ownerTokenRef.current)
      ownerTokenRef.current = null
      setPhaseSafe('ERROR')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, src, assetId])

  const visibleSurface = computeVisibleSurface({
    phase,
    showVideo: presentation.showVideo,
    showPoster: presentation.showPoster,
    posterLoaded,
    posterBroken,
  })

  const claimAndStart = (owner: Exclude<PlaybackOwnerKind, 'NONE' | 'MODAL'>, asMuted: boolean) => {
    if (getPlaybackOwner(assetId) === 'MODAL') return
    suppressOwnerNotifyRef.current = true
    releasePlayback(assetId, ownerTokenRef.current)
    ownerTokenRef.current = claimPlayback(assetId, owner, 0)
    suppressOwnerNotifyRef.current = false
    hoverActiveRef.current = owner === 'CANVAS_HOVER'
    setMuted(asMuted)
    setVideoDrawable(false)
    setPhaseSafe('STARTING')
  }

  const upgradeToExplicitPlayback = useEffectEvent((asMuted = true) => {
    if (getPlaybackOwner(assetId) === 'MODAL') return
    hoverActiveRef.current = false
    const video = videoRef.current
    suppressOwnerNotifyRef.current = true
    releasePlayback(assetId, ownerTokenRef.current)
    ownerTokenRef.current = claimPlayback(assetId, 'CANVAS_EXPLICIT', video?.currentTime || 0)
    suppressOwnerNotifyRef.current = false
    setMuted(asMuted)
    if (video) video.muted = asMuted
    if (video && video.paused && phaseRef.current !== 'STARTING') {
      void video.play().catch((err: unknown) => {
        if ((err as { name?: string } | null)?.name === 'AbortError') return
      })
    }
  })

  const activateStickyPlayback = useEffectEvent(() => {
    if (getPlaybackOwner(assetId) === 'MODAL') return
    if (phaseRef.current === 'IDLE_POSTER' || phaseRef.current === 'ERROR') {
      claimAndStart('CANVAS_EXPLICIT', true)
      return
    }
    if (
      phaseRef.current === 'STARTING' ||
      phaseRef.current === 'PLAYING' ||
      phaseRef.current === 'PAUSED' ||
      hoverActiveRef.current
    ) {
      upgradeToExplicitPlayback(true)
    }
  })

  useImperativeHandle(ref, () => ({
    activateStickyPlayback: () => activateStickyPlayback(),
  }))

  useEffect(() => {
    if (!stickyPlayback) return
    if (
      phaseRef.current === 'STARTING' ||
      phaseRef.current === 'PLAYING' ||
      phaseRef.current === 'PAUSED' ||
      hoverActiveRef.current
    ) {
      upgradeToExplicitPlayback(true)
    }
  }, [stickyPlayback])

  useEffect(() => {
    if (playbackResetKey <= 0) return
    if (!stickyPlaybackRef.current) {
      releaseToIdle({ publishTime: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbackResetKey])

  const handlePlayClick = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (hoverActiveRef.current && (phase === 'STARTING' || phase === 'PLAYING')) {
      // Upgrade in-flight muted hover preview to sticky unmuted explicit play.
      hoverActiveRef.current = false
      setMuted(false)
      const video = videoRef.current
      if (video) video.muted = false
      suppressOwnerNotifyRef.current = true
      releasePlayback(assetId, ownerTokenRef.current)
      ownerTokenRef.current = claimPlayback(assetId, 'CANVAS_EXPLICIT', video?.currentTime || 0)
      suppressOwnerNotifyRef.current = false
      return
    }

    if (phase === 'PLAYING' || (phase === 'STARTING' && presentation.showVideo)) {
      // Explicit pause via icon — keep decoded frame (PAUSED_FRAME_INVARIANT).
      hoverActiveRef.current = false
      ignorePauseEventRef.current = false
      const video = videoRef.current
      if (video && !video.paused) {
        publishPlaybackTime(assetId, video.currentTime || 0)
        video.pause()
      }
      setPhaseSafe('PAUSED')
      if (ownerTokenRef.current) {
        suppressOwnerNotifyRef.current = true
        releasePlayback(assetId, ownerTokenRef.current)
        ownerTokenRef.current = claimPlayback(assetId, 'CANVAS_EXPLICIT', videoRef.current?.currentTime || 0)
        suppressOwnerNotifyRef.current = false
      }
      return
    }

    if (phase === 'PAUSED') {
      hoverActiveRef.current = false
      setMuted(false)
      const video = videoRef.current
      if (video) video.muted = false
      suppressOwnerNotifyRef.current = true
      releasePlayback(assetId, ownerTokenRef.current)
      ownerTokenRef.current = claimPlayback(assetId, 'CANVAS_EXPLICIT', video?.currentTime || 0)
      suppressOwnerNotifyRef.current = false
      void video?.play().catch((err: unknown) => {
        if ((err as { name?: string } | null)?.name === 'AbortError') return
        setPhaseSafe('ERROR')
      })
      return
    }

    claimAndStart('CANVAS_EXPLICIT', false)
  }

  const tryHoverStart = () => {
    if (suppressHoverUntilLeaveRef.current) return
    // V9: allow hover to self-heal out of ERROR (e.g. a stale rejected
    // play() from a prior fast enter/leave) instead of requiring an
    // explicit Play-icon click to ever recover on that node again.
    if (phaseRef.current !== 'IDLE_POSTER' && phaseRef.current !== 'ERROR') return
    if (getPlaybackOwner(assetId) === 'MODAL') return
    claimAndStart('CANVAS_HOVER', true)
  }

  const handlePointerEnter = (e: PointerEvent) => {
    // Prefer real pointer evidence; fall back to hover media query for mice that
    // report incorrectly under some remote-desktop / VM environments.
    if (e.pointerType === 'touch' || e.pointerType === 'pen') return
    if (e.pointerType !== 'mouse' && !isHoverCapableEnvironment()) return
    tryHoverStart()
  }

  const handleMouseEnter = () => {
    // Secondary path for environments that synthesize mouseenter without pointer events.
    if (!isHoverCapableEnvironment()) return
    tryHoverStart()
  }

  const handlePointerLeave = () => {
    // V9 FIX (MODAL_CLOSE_AUTO_RESUME root cause): while MODAL owns this
    // asset, its mask/overlay covering this node makes the browser
    // synthesize a mouseleave/pointerleave HERE too (Chrome recomputes the
    // topmost element under a stationary pointer on every DOM mutation —
    // no real cursor movement involved). That is not a genuine user leave;
    // clearing the suppression flag on it defeats the whole point of
    // `suppressHoverUntilLeaveRef` and was why hover resumed as soon as the
    // modal closed and the browser re-fired the matching synthetic enter
    // over the same still-stationary cursor. Only a leave that happens
    // while we are NOT yielded to MODAL is a real "the user moved the
    // mouse away" signal.
    if (getPlaybackOwner(assetId) !== 'MODAL') {
      suppressHoverUntilLeaveRef.current = false
    }
    if (stickyPlaybackRef.current || isNodeDraggingRef.current) return
    if (!hoverActiveRef.current) return
    releaseToIdle({ publishTime: true })
  }

  const overlayClassName = [
    'pcv-video-play-overlay',
    'nodrag',
    'nopan',
    phase === 'PAUSED' || phase === 'PLAYING' ? 'pcv-video-play-overlay--paused-frame' : '',
    phase === 'PLAYING' ? 'pcv-video-play-overlay--playing' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={`pcv-video-preview-wrap ${NODE_MEDIA_SURFACE_CLASS}`}
      data-pcv-video-phase={phase}
      data-visible-surface={visibleSurface}
      data-pcv-playback-owner={getPlaybackOwner(assetId)}
      data-pcv-asset-id={assetId}
      data-pcv-sticky-playback={stickyPlayback ? 'true' : 'false'}
      data-pcv-node-dragging={isNodeDragging ? 'true' : 'false'}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handlePointerLeave}
    >
      {presentation.showPoster && poster ? (
        // VistoeraTASK-00139 — deliberately NO `loading="lazy"` here: this
        // `<img>` already only ever gets a real `src` once
        // `videoPosterLoadGate` grants a slot (see `gatedPosterSrc`
        // above), which is a stronger, explicit admission control than
        // the browser's own viewport-proximity heuristic. Combining both
        // was a real regression found in this task's own live-Preview
        // re-verification: a slot granted to an off-screen node under
        // native `loading="lazy"` never fires `onLoad`/`onError` (the
        // browser defers starting that fetch indefinitely), so
        // `releasePosterSlotOnce` never ran — the gate's fixed 4 slots
        // filled up with off-screen images and permanently starved every
        // other (including fully on-screen) node's poster forever.
        <img
          src={gatedPosterSrc}
          alt=""
          className={`pcv-video-preview-poster${posterLoaded && !posterBroken ? ' pcv-video-preview-poster--ready' : ''}`}
          draggable={false}
          decoding="async"
          onLoad={() => {
            setPosterLoaded(true)
            releasePosterSlotOnce()
          }}
          onError={() => {
            setPosterBroken(true)
            releasePosterSlotOnce()
          }}
        />
      ) : null}
      {presentation.showLoadingPlaceholder ? (
        // VistoeraTASK-00139 — a *terminal* poster failure (backend 404
        // "首帧缩略图暂不可用", or a broken/expired URL) must render a static
        // surface, not the perpetual shimmer meant for a genuinely
        // in-flight backfill — otherwise a permanently-unavailable poster
        // "flashes" forever with no way it could ever resolve.
        <div
          className={`pcv-video-preview-prime-placeholder${posterBroken ? ' pcv-video-preview-prime-placeholder--static' : ''}`}
          aria-hidden="true"
        />
      ) : null}
      {presentation.mountVideo ? (
        <video
          ref={videoRef}
          className={className}
          src={src}
          preload="auto"
          controls={false}
          playsInline
          muted={muted}
          draggable={false}
          // Clicks on the video itself do NOT toggle playback (V8) — reserved for
          // node select/expand on the media body. Play icon owns play/pause.
          style={
            presentation.showVideo
              ? { pointerEvents: 'none' }
              : { opacity: 0, position: 'absolute', inset: 0, pointerEvents: 'none' }
          }
        />
      ) : null}
      {presentation.showPlay ? (
        <button
          type="button"
          className={overlayClassName}
          aria-label={phase === 'PLAYING' ? 'Pause' : 'Play'}
          onClick={handlePlayClick}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <span className="pcv-video-play-icon" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}

export default forwardRef(CanvasVideoPreview)
